/**
 * KeywordContext - Keyword Flagging System
 *
 * Manages keyword groups for flagging suspicious activity in the timeline.
 * Keywords are synced with the desktop app via WebRTC and cached in localStorage.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useWebRTC } from './WebRTCContext';

const KeywordContext = createContext(null);

// Default keyword groups with preset examples
const DEFAULT_KEYWORD_GROUPS = [
  {
    id: 'adult-content',
    name: 'Adult Content',
    color: '#ef4444',
    enabled: true,
    keywords: ['pornhub', 'xvideos', 'onlyfans', 'chaturbate', 'xhamster', 'redtube']
  },
  {
    id: 'job-search',
    name: 'Job Search',
    color: '#3b82f6',
    enabled: false,
    keywords: ['linkedin jobs', 'indeed', 'glassdoor', 'career', 'resume', 'job application']
  },
  {
    id: 'gaming',
    name: 'Gaming',
    color: '#8b5cf6',
    enabled: false,
    keywords: ['steam', 'discord', 'twitch', 'fortnite', 'minecraft', 'valorant', 'league of legends']
  },
  {
    id: 'social-media',
    name: 'Social Media',
    color: '#ec4899',
    enabled: false,
    keywords: ['facebook', 'instagram', 'twitter', 'tiktok', 'snapchat', 'reddit']
  }
];

const STORAGE_KEY = 'inquisitor-keyword-groups';

export function KeywordProvider({ children }) {
  const { isConnected, sendFeatureMessage, featureManager } = useWebRTC();
  const [keywordGroups, setKeywordGroups] = useState(() => {
    // Load from localStorage cache or use defaults
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error('[KeywordContext] Failed to load from localStorage:', e);
    }
    return DEFAULT_KEYWORD_GROUPS;
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSynced, setIsSynced] = useState(false);
  const pendingCallbacksRef = useRef(new Map());

  // Send message to desktop keyword-database feature
  const sendKeywordMessage = useCallback((action, payload = {}) => {
    return new Promise((resolve, reject) => {
      const messageId = Math.random().toString(36).substring(2, 15);

      // Store callback
      pendingCallbacksRef.current.set(messageId, { resolve, reject });

      // Timeout after 10 seconds
      setTimeout(() => {
        if (pendingCallbacksRef.current.has(messageId)) {
          pendingCallbacksRef.current.delete(messageId);
          reject(new Error('Timeout waiting for response'));
        }
      }, 10000);

      sendFeatureMessage({
        feature: 'keyword-database',
        action,
        payload,
        messageId
      });
    });
  }, [sendFeatureMessage]);

  // Handle response from desktop
  useEffect(() => {
    if (!featureManager) return;

    const keywordFeature = featureManager.getFeature?.('keyword-database');
    if (keywordFeature && !keywordFeature._keywordContextHandler) {
      keywordFeature._keywordContextHandler = true;

      // Register response handler
      const originalHandleMessage = keywordFeature.handleMessage?.bind(keywordFeature);
      keywordFeature.handleMessage = async (message) => {
        // The reply's correlation id lives at the TOP level (message.messageId),
        // not inside payload — reading only message.payload.messageId meant every
        // request timed out and the panel silently kept its cached defaults.
        const body = message.payload || message;
        const messageId = message.messageId ?? body.messageId;
        const { success, groups, group, ...rest } = body;

        // Check if this is a response to a pending request
        if (messageId && pendingCallbacksRef.current.has(messageId)) {
          const { resolve, reject } = pendingCallbacksRef.current.get(messageId);
          pendingCallbacksRef.current.delete(messageId);

          if (success === false) {
            reject(new Error(rest.error || 'Request failed'));
          } else {
            resolve({ success, groups, group, ...rest });
          }
          return;
        }

        // Call original handler if exists
        if (originalHandleMessage) {
          return originalHandleMessage(message);
        }
      };
    }
  }, [featureManager]);

  // Load keywords from desktop on connect
  useEffect(() => {
    if (!isConnected) {
      setIsLoading(false);
      setIsSynced(false);
      return;
    }

    const loadFromDesktop = async () => {
      setIsLoading(true);
      // The keyword-database feature registers a beat AFTER the peer connects,
      // so a single attempt on `isConnected` often loses the race and falls back
      // to cached defaults. Retry until the feature answers (verified path:
      // kf.getAllGroups round-trips; the bespoke sendKeywordMessage did not).
      let result = null;
      for (let attempt = 0; attempt < 6 && !result?.groups; attempt++) {
        const kf = featureManager?.getFeature?.('keyword-database');
        if (kf?.getAllGroups) {
          try { result = await kf.getAllGroups(); } catch { /* not ready — retry */ }
        }
        if (!result?.groups) await new Promise(r => setTimeout(r, 1000));
      }

      try {
        if (result?.groups && result.groups.length > 0) {
          setKeywordGroups(result.groups);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(result.groups));
          setIsSynced(true);
          console.log('[KeywordContext] Loaded', result.groups.length, 'groups from desktop');
        } else if (result?.groups) {
          // Desktop genuinely has no groups (empty array) — seed our defaults.
          console.log('[KeywordContext] No groups on desktop, syncing defaults...');
          const kf = featureManager?.getFeature?.('keyword-database');
          for (const group of keywordGroups) {
            kf?.upsertGroup ? await kf.upsertGroup(group) : await sendKeywordMessage('upsert_group', group);
          }
          setIsSynced(true);
        } else {
          // Never reached the desktop — DON'T overwrite; keep cache for now.
          console.warn('[KeywordContext] Desktop keyword store unreachable after retries; keeping cache');
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadFromDesktop();
  }, [isConnected]);

  // Persist to localStorage as cache
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(keywordGroups));
    } catch (e) {
      console.error('[KeywordContext] Failed to save to localStorage:', e);
    }
  }, [keywordGroups]);

  // Get only enabled groups
  const enabledGroups = useMemo(() => {
    return keywordGroups.filter(g => g.enabled);
  }, [keywordGroups]);

  // Check if text matches any keyword from enabled groups
  // Returns { matched: boolean, groups: [{ id, name, color, keyword }] }
  const checkKeywords = useCallback((text) => {
    if (!text || enabledGroups.length === 0) {
      return { matched: false, groups: [] };
    }

    const lowerText = text.toLowerCase();
    const matchedGroups = [];

    for (const group of enabledGroups) {
      for (const keyword of group.keywords) {
        if (lowerText.includes(keyword.toLowerCase())) {
          matchedGroups.push({
            id: group.id,
            name: group.name,
            color: group.color,
            keyword: keyword
          });
          break; // Only match once per group
        }
      }
    }

    return {
      matched: matchedGroups.length > 0,
      groups: matchedGroups
    };
  }, [enabledGroups]);

  // Add a new keyword group
  const addKeywordGroup = useCallback(async (group) => {
    const newGroup = {
      id: `group-${Date.now()}`,
      name: group.name || 'New Group',
      color: group.color || '#f59e0b',
      enabled: true,
      keywords: group.keywords || []
    };

    // Optimistic update
    setKeywordGroups(prev => [...prev, newGroup]);

    // Sync to desktop if connected
    if (isConnected) {
      try {
        await sendKeywordMessage('upsert_group', newGroup);
      } catch (error) {
        console.error('[KeywordContext] Failed to sync add to desktop:', error);
      }
    }

    return newGroup;
  }, [isConnected, sendKeywordMessage]);

  // Update a keyword group
  const updateKeywordGroup = useCallback(async (id, updates) => {
    let updatedGroup = null;

    // Optimistic update
    setKeywordGroups(prev => prev.map(g => {
      if (g.id === id) {
        updatedGroup = { ...g, ...updates };
        return updatedGroup;
      }
      return g;
    }));

    // Sync to desktop if connected
    if (isConnected && updatedGroup) {
      try {
        await sendKeywordMessage('upsert_group', updatedGroup);
      } catch (error) {
        console.error('[KeywordContext] Failed to sync update to desktop:', error);
      }
    }
  }, [isConnected, sendKeywordMessage]);

  // Delete a keyword group
  const deleteKeywordGroup = useCallback(async (id) => {
    // Optimistic update
    setKeywordGroups(prev => prev.filter(g => g.id !== id));

    // Sync to desktop if connected
    if (isConnected) {
      try {
        await sendKeywordMessage('delete_group', { groupId: id });
      } catch (error) {
        console.error('[KeywordContext] Failed to sync delete to desktop:', error);
      }
    }
  }, [isConnected, sendKeywordMessage]);

  // Toggle group enabled state
  const toggleKeywordGroup = useCallback(async (id) => {
    let updatedGroup = null;

    setKeywordGroups(prev => prev.map(g => {
      if (g.id === id) {
        updatedGroup = { ...g, enabled: !g.enabled };
        return updatedGroup;
      }
      return g;
    }));

    // Sync to desktop if connected
    if (isConnected && updatedGroup) {
      try {
        await sendKeywordMessage('upsert_group', updatedGroup);
      } catch (error) {
        console.error('[KeywordContext] Failed to sync toggle to desktop:', error);
      }
    }
  }, [isConnected, sendKeywordMessage]);

  // Add keyword to a group
  const addKeyword = useCallback(async (groupId, keyword) => {
    if (!keyword.trim()) return;

    let updatedGroup = null;

    setKeywordGroups(prev => prev.map(g => {
      if (g.id === groupId) {
        updatedGroup = { ...g, keywords: [...g.keywords, keyword.trim().toLowerCase()] };
        return updatedGroup;
      }
      return g;
    }));

    // Sync to desktop if connected
    if (isConnected && updatedGroup) {
      try {
        await sendKeywordMessage('upsert_group', updatedGroup);
      } catch (error) {
        console.error('[KeywordContext] Failed to sync add keyword to desktop:', error);
      }
    }
  }, [isConnected, sendKeywordMessage]);

  // Remove keyword from a group
  const removeKeyword = useCallback(async (groupId, keyword) => {
    let updatedGroup = null;

    setKeywordGroups(prev => prev.map(g => {
      if (g.id === groupId) {
        updatedGroup = { ...g, keywords: g.keywords.filter(k => k !== keyword) };
        return updatedGroup;
      }
      return g;
    }));

    // Sync to desktop if connected
    if (isConnected && updatedGroup) {
      try {
        await sendKeywordMessage('upsert_group', updatedGroup);
      } catch (error) {
        console.error('[KeywordContext] Failed to sync remove keyword to desktop:', error);
      }
    }
  }, [isConnected, sendKeywordMessage]);

  // Reset to defaults
  const resetToDefaults = useCallback(async () => {
    setKeywordGroups(DEFAULT_KEYWORD_GROUPS);

    // Sync to desktop if connected
    if (isConnected) {
      try {
        // Delete all existing groups first
        for (const group of keywordGroups) {
          await sendKeywordMessage('delete_group', { groupId: group.id });
        }
        // Add default groups
        for (const group of DEFAULT_KEYWORD_GROUPS) {
          await sendKeywordMessage('upsert_group', group);
        }
      } catch (error) {
        console.error('[KeywordContext] Failed to sync reset to desktop:', error);
      }
    }
  }, [isConnected, sendKeywordMessage, keywordGroups]);

  // Refresh from desktop
  const refreshFromDesktop = useCallback(async () => {
    if (!isConnected) return;

    try {
      setIsLoading(true);
      const kf = featureManager?.getFeature?.('keyword-database');
      const result = kf?.getAllGroups
        ? await kf.getAllGroups()
        : await sendKeywordMessage('get_all_groups');
      if (result.groups) {
        setKeywordGroups(result.groups);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(result.groups));
      }
    } catch (error) {
      console.error('[KeywordContext] Failed to refresh from desktop:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isConnected, sendKeywordMessage]);

  const value = {
    keywordGroups,
    enabledGroups,
    checkKeywords,
    addKeywordGroup,
    updateKeywordGroup,
    deleteKeywordGroup,
    toggleKeywordGroup,
    addKeyword,
    removeKeyword,
    resetToDefaults,
    refreshFromDesktop,
    isLoading,
    isSynced,
    isConnected
  };

  return (
    <KeywordContext.Provider value={value}>
      {children}
    </KeywordContext.Provider>
  );
}

export function useKeywords() {
  const context = useContext(KeywordContext);
  if (!context) {
    throw new Error('useKeywords must be used within a KeywordProvider');
  }
  return context;
}

export default KeywordContext;
